/**
 * Review submission (§7.2). Double-blind, 14-day window.
 *
 * The screen says so plainly: a review is not visible until both sides have
 * written one or the window closes, which is what stops the first reviewer from
 * anchoring the second.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi, useMutation } from '../../hooks/useApi';
import type { Rental } from '../../api/types';
import { Card, Field, Loading, LiveRegion } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { EmptyState } from '../../components/EmptyState';
import { localDateTime } from '../../lib/format';
import { routes } from '../../api/routes';

export function RentalReview() {
  const { rentalId = '' } = useParams();
  const rental = useApi<Rental>(routes.rentals.one(rentalId));
  const submit = useMutation<Record<string, unknown>, unknown>(routes.rentals.review(rentalId));
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState('');

  if (rental.initialLoading) return <Loading label="Loading" />;
  if (rental.problem) return <ErrorView problem={rental.problem} onRetry={rental.reload} />;

  const review = rental.data?.review ?? null;

  if (!review) {
    return (
      <Card title="Review">
        <EmptyState
          heading="No review to write"
          body="Reviews open when a rental ends and close 14 days later."
        />
      </Card>
    );
  }

  if (review.submitted_at) {
    return (
      <Card title="Review">
        <p>You submitted your review on {localDateTime(review.submitted_at)}.</p>
        <p className="muted">
          It stays hidden until the other side writes theirs, or until the window closes on{' '}
          {localDateTime(review.window_closes_at)}.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Write a review">
      <p className="muted">
        Double-blind: neither review is shown until both are in, or until the window closes on{' '}
        {localDateTime(review.window_closes_at)}.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit.run({ rating, body });
        }}
      >
        <Field id="rev-rating" label="Rating, 1 to 5">
          <input
            id="rev-rating"
            className="input input-narrow"
            type="number"
            min={1}
            max={5}
            value={rating}
            onChange={(e) => setRating(Number(e.target.value))}
          />
        </Field>
        <Field id="rev-body" label="What happened">
          <textarea
            id="rev-body"
            className="textarea"
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </Field>
        {submit.problem ? <InlineError problem={submit.problem} /> : null}
        <button type="submit" className="btn btn-primary" disabled={submit.pending}>
          {submit.pending ? 'Submitting…' : 'Submit the review'}
        </button>
        {submit.data ? <LiveRegion>Submitted.</LiveRegion> : null}
      </form>
    </Card>
  );
}
